import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaCheckCircle,
  FaPrint,
  FaArrowRight,
  FaHome,
  FaEnvelope,
  FaPhone,
  FaTimesCircle,
} from "react-icons/fa";
import Header from "./header";
import Footer from "./Footer";
import { Helmet } from "react-helmet";

const styles = {
  page: { background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" },
  container: { maxWidth: "800px", margin: "0 auto", padding: "100px 20px" },

  successCard: {
    background: "#fff",
    borderRadius: "16px",
    padding: "32px",
    textAlign: "center",
    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
    marginBottom: "24px",
  },
  icon: { color: "#10b981", fontSize: "60px", marginBottom: "12px" },
  title: { fontSize: "26px", fontWeight: "800", color: "#0f172a" },
  subtitle: { color: "#64748b" },

  card: {
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid #e2e8f0",
  },

  section: { marginBottom: "24px" },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "700",
    marginBottom: "12px",
    color: "#334155",
  },

  row: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px dashed #e2e8f0",
  },

  label: { color: "#64748b" },
  value: { fontWeight: "600", color: "#0f172a" },

  btnGroup: {
    display: "flex",
    gap: "16px",
    justifyContent: "center",
    marginTop: "32px",
  },
  primaryBtn: {
    background: "#0f172a",
    color: "#fff",
    padding: "12px 24px",
    borderRadius: "8px",
    border: "none",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  secondaryBtn: {
    background: "#fff",
    border: "1px solid #cbd5e1",
    padding: "12px 24px",
    borderRadius: "8px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
};
const districtsData = JSON.parse(localStorage.getItem("districts") || "[]");
const savedCities = JSON.parse(localStorage.getItem("cities") || "[]");
const statesData = JSON.parse(localStorage.getItem("states") || "[]");

const OrderDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { orderId } = useParams();

  const order = state?.order;

  useEffect(() => {
    if (!order) navigate("/");
  }, [order, navigate]);

  if (!order) return null;

  const cityObj = savedCities.find(
    (city) => String(city.id) === String(order.city),
  );
  const cityName = cityObj?.name || "—";
  return (
    <>
      <Helmet>
        <title>
          {order?.status === "SUCCESS"
            ? `Order Confirmed #${orderId} | CGRMART`
            : `Payment Failed #${orderId} | CGRMART`}
        </title>

        <meta
          name="description"
          content={
            order?.status === "SUCCESS"
              ? `Your order ${orderId} has been successfully placed. View order details, payment info, and shipping details.`
              : `Payment failed for order ${orderId}. Please retry or contact support.`
          }
        />
        
        <meta name="author" content="CGRMART" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={
            order?.status === "SUCCESS"
              ? `Order Confirmed #${orderId}`
              : `Payment Failed #${orderId}`
          }
        />
        <meta
          property="og:description"
          content={
            order?.status === "SUCCESS"
              ? "Your order has been successfully placed."
              : "Your payment could not be processed."
          }
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />

        {/* Twitter */}
        <meta
          name="twitter:title"
          content={
            order?.status === "SUCCESS"
              ? `Order Confirmed #${orderId}`
              : `Payment Failed #${orderId}`
          }
        />
        <meta
          name="twitter:description"
          content={
            order?.status === "SUCCESS"
              ? "Order placed successfully."
              : "Payment failed. Please try again."
          }
        />

        {/* 🔒 IMPORTANT */}
        

        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <Header />

      <style>{`
        @media (max-width: 768px) {
          .order-details-container { max-width: 100% !important; padding: 30px 15px !important; }
          .order-details-card { padding: 20px !important; }
          .order-details-icon { font-size: 48px !important; }
          .order-details-title { font-size: 22px !important; }
          .order-details-btn-group { flex-direction: column !important; }
          .order-details-btn-group button { width: 100% !important; }
        }
        @media (max-width: 480px) {
          .order-details-container { padding: 70px 10px !important; }
          .order-details-card { padding: 15px !important; }
          .order-details-icon { font-size: 40px !important; }
          .order-details-title { font-size: 20px !important; }
        }
      `}</style>
      <div style={styles.page}>
        <div style={styles.container} className="order-details-container">
          {/* SUCCESS */}
          {/* PAYMENT STATUS CARD */}
          <div style={styles.successCard} className="order-details-card">
            {order.status === "SUCCESS" ? (
              <>
                <FaCheckCircle
                  style={styles.icon}
                  className="order-details-icon"
                />
                <h1 style={styles.title} className="order-details-title">
                  Order Confirmed
                </h1>
                <p style={styles.subtitle}>Your payment was successful</p>
                <div
                  style={{
                    marginTop: "10px",
                    display: "inline-block",
                    padding: "6px 14px",
                    background: "#ecfdf5",
                    borderRadius: "20px",
                    color: "#065f46",
                    fontWeight: "600",
                  }}
                >
                  Order ID: #{orderId}
                </div>
              </>
            ) : (
              <>
                <FaTimesCircle
                  style={{ ...styles.icon, color: "#dc2626" }}
                  className="order-details-icon"
                />
                <h1 style={styles.title} className="order-details-title">
                  Payment Failed
                </h1>
                <p style={styles.subtitle}>
                  Your payment could not be processed
                </p>
                <div
                  style={{
                    marginTop: "10px",
                    display: "inline-block",
                    padding: "6px 14px",
                    background: "#fee2e2",
                    borderRadius: "20px",
                    color: "#b91c1c",
                    fontWeight: "600",
                  }}
                >
                  Order ID: #{orderId}
                </div>
              </>
            )}
          </div>

          {/* DETAILS */}
          <div style={styles.card}>
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Product Details</div>
              {order.products?.map((product, index) => (
                <div key={index}>
                  <div style={styles.row}>
                    <span style={styles.label}>Product</span>
                    <span style={styles.value}>{product.proname}</span>
                  </div>

                  <div style={styles.row}>
                    <span style={styles.label}>Quantity</span>
                    <span style={styles.value}>{product.count}</span>
                  </div>

                  <div style={styles.row}>
                    <span style={styles.label}>Unit Price (GST Included)</span>
                    <span style={styles.value}>
                      ₹{product.price / product.count}
                    </span>
                  </div>

                  <div style={styles.row}>
                    <span style={styles.label}>Total Price (GST Included)</span>
                    <span style={styles.value}>₹{product.price}</span>
                  </div>

                  {index !== order.products.length - 1 && (
                    <div
                      style={{
                        borderBottom: "2px solid #e2e8f0",
                        margin: "10px 0",
                      }}
                    />
                  )}
                </div>
              ))}

              <div
                style={{
                  borderBottom: "2px solid #e2e8f0",
                  margin: "10px 0",
                }}
              />

              {/* Subtotal */}
              <div style={styles.row}>
                <span style={styles.label}>Subtotal (GST Included)</span>
                <span style={styles.value}>
                  ₹{order.products.reduce((t, p) => t + Number(p.price), 0)}
                </span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Shipping Price</span>
                <span style={styles.value}>
                  ₹
                  {order.amount -
                    order.products.reduce((t, p) => t + Number(p.price), 0)}
                </span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Total Paid (GST Included)</span>
                <span style={styles.value}>₹{order.amount}</span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Status</span>
                <span
                  style={{
                    fontWeight: "700",
                    color: order.status === "SUCCESS" ? "#16a34a" : "#dc2626",
                  }}
                >
                  {order.status ?? "FAILED"}
                </span>
              </div>
            </div>

            <div style={styles.section}>
              <div style={styles.sectionTitle}>Shipping Details</div>

              <div style={styles.row}>
                <span style={styles.label}>Name</span>
                <span style={styles.value}>{order.name}</span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Address</span>
                <span style={styles.value}>
                  {order.address}, {cityName} - {order.pincode}
                </span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Mobile</span>
                <span style={styles.value}>{order.mobile}</span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Email</span>
                <span style={styles.value}>{order.email}</span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Payment ID</span>
                <span style={styles.value}>{order.pay_id}</span>
              </div>
            </div>
          </div>

          {/* ACTIONS */}
          <div style={styles.btnGroup} className="order-details-btn-group">
            <button style={styles.primaryBtn} onClick={() => navigate("/")}>
              <FaHome /> Continue Shopping <FaArrowRight />
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default OrderDetails;
